import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, Row, Col, message } from 'antd';
import { searchItems, useGetAllItems } from '../../hooks/store/itemQuery';
import { useGetAllStores } from '../../hooks/store/storeQuery';
import { getStoreDepartments, useGetAllStoreDepartments } from '../../hooks/store/storeDepartmentQuery';

const { Option } = Select;
const { TextArea } = Input;

const TransactionModal = ({ visible, onCancel, onSubmit, title }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sourceDepartments, setSourceDepartments] = useState([]);
  const [destDepartments, setDestDepartments] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSourceStore, setSelectedSourceStore] = useState(null);
  const [selectedDestStore, setSelectedDestStore] = useState(null);
  const [itemLoadFlag, setItemLoadFlag] = useState(false);
  
  const { stores } = useGetAllStores();
  const { departments: allSourceDepts } = useGetAllStoreDepartments(selectedSourceStore, itemLoadFlag);
  const { departments: allDestDepts } = useGetAllStoreDepartments(selectedDestStore, itemLoadFlag);

  const transactionTypes = [
    { value: 'PURCHASE', label: 'Purchase' },
    { value: 'SALE', label: 'Sale' },
    { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' },
    { value: 'RETURN_FROM_CUSTOMER', label: 'Return from Customer' },
    { value: 'ADJUSTMENT', label: 'Adjustment' },
    { value: 'TRANSFER', label: 'Transfer' },
    { value: 'WRITE_OFF', label: 'Write Off' },
    { value: 'LOCATION_TRANSFER', label: 'Location Transfer' },
    { value: 'STORE_RECEIPT', label: 'Store Receipt' },
    { value: 'STORE_SHIPMENT', label: 'Store Shipment' },
  ];

  const locationTypes = [
    { value: 'STORE', label: 'Store' },
    { value: 'DEPARTMENT', label: 'Department' },
  ];

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setItems([]);
      setSelectedItem(null);
      setSourceDepartments([]);
      setDestDepartments([]);
      setSelectedSourceStore(null);
      setSelectedDestStore(null);
    } else {
      // Load some initial items when modal opens
      loadInitialItems();
    }
  }, [visible, form]);

  // Update departments when stores change
  useEffect(() => {
    setSourceDepartments(allSourceDepts || []);
  }, [allSourceDepts]);

  useEffect(() => {
    setDestDepartments(allDestDepts || []);
  }, [allDestDepts]);

  const loadInitialItems = async () => {
    setSearchLoading(true);
    try {
      // Try loading items with a wildcard or common search term
      const data = await searchItems('item', 0, 50); // Search for 'item' to get general results
      if (data && data.content) {
        setItems(data.content);
      } else {
        // If search doesn't work, try loading items without search
        setItems([]);
      }
    } catch (error) {
      console.error('Failed to load initial items:', error);
      setItems([]);
    }
    setSearchLoading(false);
  };

  const handleSearch = async (value) => {
    if (value && value.length > 2) {
      setSearchLoading(true);
      try {
        const data = await searchItems(value, 0, 20);
        if (data && data.content) {
          setItems(data.content);
        }
      } catch (error) {
        message.error('Failed to search items');
      }
      setSearchLoading(false);
    }
  };

  const handleItemSelect = (value) => {
    const item = items.find(i => i.id === value);
    setSelectedItem(item);
    if (item) {
      form.setFieldsValue({ unitPrice: item.unitPrice });
    }
  };

  const handleSourceStoreChange = (storeId) => {
    setSelectedSourceStore(storeId);
    form.setFieldsValue({ sourceStoreDepartmentId: undefined });
    setItemLoadFlag(!itemLoadFlag); // Trigger department reload
  };

  const handleDestStoreChange = (storeId) => {
    setSelectedDestStore(storeId);
    form.setFieldsValue({ destinationStoreDepartmentId: undefined });
    setItemLoadFlag(!itemLoadFlag); // Trigger department reload
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSubmit(values);
      form.resetFields();
      onCancel();
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const transactionType = Form.useWatch('transactionType', form);
  const showLocationFields = ['TRANSFER', 'LOCATION_TRANSFER', 'STORE_RECEIPT', 'STORE_SHIPMENT'].includes(transactionType);

  return (
    <Modal
      title={title}
      open={visible}
      onOk={handleOk}
      onCancel={onCancel}
      width={800}
      confirmLoading={loading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          quantity: 1,
          sourceLocationType: 'STORE',
          destinationLocationType: 'STORE',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="itemId"
              label="Item"
              rules={[{ required: true, message: 'Please select an item' }]}
            >
              <Select
                showSearch
                placeholder="Type to search or select from available items"
                onSearch={handleSearch}
                onChange={handleItemSelect}
                filterOption={false}
                loading={searchLoading}
                notFoundContent={searchLoading ? 'Searching...' : 'Type to search for items or check if items are available in the system'}
                onDropdownVisibleChange={(open) => {
                  if (open && items.length === 0) {
                    loadInitialItems();
                  }
                }}
              >
                {items.map(item => (
                  <Option key={item.id} value={item.id}>
                    {item.name} - {item.storeDepartmentName} (Stock: {item.quantityInStock || 0})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="transactionType"
              label="Transaction Type"
              rules={[{ required: true, message: 'Please select transaction type' }]}
            >
              <Select placeholder="Select transaction type">
                {transactionTypes.map(type => (
                  <Option key={type.value} value={type.value}>
                    {type.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="quantity"
              label="Quantity"
              rules={[
                { required: true, message: 'Please enter quantity' },
                { type: 'number', min: 0.01, message: 'Quantity must be greater than 0' }
              ]}
            >
              <InputNumber
                min={0.01}
                style={{ width: '100%' }}
                placeholder="Enter quantity"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="unitPrice"
              label="Unit Price"
            >
              <InputNumber
                min={0}
                style={{ width: '100%' }}
                placeholder="Enter unit price"
                prefix="$"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="referenceNumber"
              label="Reference Number"
            >
              <Input placeholder="Enter reference number" />
            </Form.Item>
          </Col>
        </Row>

        {showLocationFields && (
          <>
            <h4>Source Location</h4>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="sourceLocationType"
                  label="Source Type"
                >
                  <Select>
                    {locationTypes.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="sourceStoreId"
                  label="Source Store"
                >
                  <Select 
                    placeholder="Select source store"
                    onChange={handleSourceStoreChange}
                    allowClear
                  >
                    {stores.map(store => (
                      <Option key={store.id} value={store.id}>
                        {store.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="sourceStoreDepartmentId"
                  label="Source Department"
                >
                  <Select placeholder="Select source department" allowClear>
                    {sourceDepartments.map(dept => (
                      <Option key={dept.id} value={dept.id}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <h4>Destination Location</h4>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="destinationLocationType"
                  label="Destination Type"
                >
                  <Select>
                    {locationTypes.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="destinationStoreId"
                  label="Destination Store"
                >
                  <Select 
                    placeholder="Select destination store"
                    onChange={handleDestStoreChange}
                    allowClear
                  >
                    {stores.map(store => (
                      <Option key={store.id} value={store.id}>
                        {store.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="destinationStoreDepartmentId"
                  label="Destination Department"
                >
                  <Select placeholder="Select destination department" allowClear>
                    {destDepartments.map(dept => (
                      <Option key={dept.id} value={dept.id}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </>
        )}

        <Form.Item
          name="notes"
          label="Notes"
        >
          <TextArea rows={3} placeholder="Enter transaction notes" />
        </Form.Item>

        {selectedItem && (
          <div style={{ marginTop: 16, padding: 16, background: '#f0f0f0', borderRadius: 4 }}>
            <h4>Item Details</h4>
            <p><strong>Current Stock:</strong> {selectedItem.quantityInStock}</p>
            <p><strong>Unit Price:</strong> ${selectedItem.unitPrice}</p>
            <p><strong>Department:</strong> {selectedItem.storeDepartmentName}</p>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default TransactionModal;